import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, TextField } from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';

export type OccupantsFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const OccupantsFormDialog: FC<
  PropsWithChildren<OccupantsFormDialogProps>
> = ({ open, control, onClose }) => (
  <SubFormDialog title="Occupants" open={open} onClose={onClose}>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          control={control}
          name="general.occupants.fullTimeEmployees"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Full-time employees"
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          control={control}
          name="general.occupants.visitors"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Visitors"
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          control={control}
          name="general.occupants.retailCustomers"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Retail customers"
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          control={control}
          name="general.occupants.k12Students"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="K–12 students"
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          control={control}
          name="general.occupants.residentialTenants"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Residential tenants"
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          control={control}
          name="general.occupants.description"
          rules={{ maxLength: 500 }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Description"
                type="text"
                variant="outlined"
                multiline
                minRows={2}
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
    </Grid>
  </SubFormDialog>
);
